import { z } from "zod";
import { idSchema, moneySchema } from "./common.js";
import { dnsRecordSchema } from "./dns.js";
import { domainNameSchema, domainPriceBreakdownSchema } from "./domain.js";

/** Registrant details as registries require them (NIS2-style identity fields). */
export const registrantSchema = z.object({
  firstName: z.string().trim().min(1).max(100),
  lastName: z.string().trim().min(1).max(100),
  organization: z.string().trim().max(200).optional(),
  email: z.email().trim().toLowerCase(),
  /** E.164, e.g. +46701234567 */
  phone: z
    .string()
    .trim()
    .regex(/^\+[1-9]\d{6,14}$/, { message: "Use the international format, e.g. +46701234567" }),
  street: z.string().trim().min(1).max(200),
  city: z.string().trim().min(1).max(100),
  postalCode: z.string().trim().min(1).max(20),
  countryCode: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{2}$/, { message: "Two-letter country code" }),
  stateOrProvince: z.string().trim().max(100).optional(),
});
export type Registrant = z.infer<typeof registrantSchema>;

export const orderItemInputSchema = z.object({
  domain: domainNameSchema,
  years: z.number().int().min(1).max(10).default(1),
});
export type OrderItemInput = z.infer<typeof orderItemInputSchema>;

export const orderQuoteRequestSchema = z.object({
  items: z.array(orderItemInputSchema).min(1).max(20),
});
export type OrderQuoteRequest = z.infer<typeof orderQuoteRequestSchema>;

export const orderQuoteItemSchema = z.object({
  domain: domainNameSchema,
  years: z.number().int(),
  /** Absent when the domain cannot be registered right now; `reason` says why. */
  provider: z.string().optional(),
  price: domainPriceBreakdownSchema.optional(),
  reason: z.enum(["unavailable", "premium", "unsupported", "unknown"]).optional(),
});
export type OrderQuoteItem = z.infer<typeof orderQuoteItemSchema>;

export const orderQuoteSchema = z.object({
  items: z.array(orderQuoteItemSchema),
  /** Sum of the registrable items; the payment page charges exactly this. */
  total: moneySchema,
  purchasable: z.boolean(),
});
export type OrderQuote = z.infer<typeof orderQuoteSchema>;

export const orderCreateRequestSchema = z.object({
  items: z.array(orderItemInputSchema).min(1).max(20),
  registrant: registrantSchema,
  whoisPrivacy: z.boolean().default(true),
});
export type OrderCreateRequest = z.infer<typeof orderCreateRequestSchema>;

export const orderStatusSchema = z.enum([
  "pending_payment",
  "paid",
  "completed",
  "partially_failed",
  "failed",
  "cancelled",
]);
export type OrderStatus = z.infer<typeof orderStatusSchema>;

export const orderItemStatusSchema = z.enum(["pending", "registered", "failed"]);
export type OrderItemStatus = z.infer<typeof orderItemStatusSchema>;

export const orderItemKindSchema = z.enum(["registration", "renewal"]);
export type OrderItemKind = z.infer<typeof orderItemKindSchema>;

export const orderItemSchema = z.object({
  id: idSchema,
  kind: orderItemKindSchema,
  domain: domainNameSchema,
  years: z.number().int(),
  provider: z.string(),
  price: domainPriceBreakdownSchema,
  status: orderItemStatusSchema,
  error: z.string().nullable(),
  expiresAt: z.iso.datetime().nullable(),
});
export type OrderItem = z.infer<typeof orderItemSchema>;

export const orderSchema = z.object({
  id: idSchema,
  status: orderStatusSchema,
  source: z.enum(["checkout", "auto_renew"]),
  total: moneySchema,
  items: z.array(orderItemSchema),
  /** Null for renewals: the registrant on file with the registrar is unchanged. */
  registrant: registrantSchema.nullable(),
  whoisPrivacy: z.boolean(),
  paymentProvider: z.string(),
  createdAt: z.iso.datetime(),
  paidAt: z.iso.datetime().nullable(),
  completedAt: z.iso.datetime().nullable(),
});
export type Order = z.infer<typeof orderSchema>;

export const orderCreatedSchema = z.object({
  order: orderSchema,
  /** Hosted payment page; the customer is sent there and comes back to the order page. */
  checkoutUrl: z.url(),
});
export type OrderCreated = z.infer<typeof orderCreatedSchema>;

export const orderListSchema = z.object({ orders: z.array(orderSchema) });
export type OrderList = z.infer<typeof orderListSchema>;

// --- Owned domains ----------------------------------------------------------------------------------

export const ownedDomainSchema = z.object({
  id: idSchema,
  name: domainNameSchema,
  tld: z.string(),
  status: z.enum([
    "pending_registration",
    "active",
    "pending_transfer",
    "expired",
    "redemption",
    "transferred_out",
    "cancelled",
    "pending_verification",
  ]),
  /** Registered through us, or registered elsewhere and connected for mail. */
  source: z.enum(["registered", "external"]),
  provider: z.string(),
  expiresAt: z.iso.datetime().nullable(),
  autoRenew: z.boolean(),
  locked: z.boolean(),
  whoisPrivacy: z.boolean(),
  /** When the domain became ready for mailboxes (template applied, or provisioned after verification). */
  mailSetupAt: z.iso.datetime().nullable(),
  /** External domains: when control of the DNS zone was proven. */
  verifiedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
});
export type OwnedDomain = z.infer<typeof ownedDomainSchema>;

export const ownedDomainListSchema = z.object({ domains: z.array(ownedDomainSchema) });
export type OwnedDomainList = z.infer<typeof ownedDomainListSchema>;

export const domainRenewRequestSchema = z.object({
  years: z.coerce.number().int().min(1).max(10).default(1),
});
export type DomainRenewRequest = z.infer<typeof domainRenewRequestSchema>;

export const domainRenewQuoteSchema = z.object({
  domain: domainNameSchema,
  years: z.number().int(),
  price: domainPriceBreakdownSchema,
  /** Expiry after the renewal is applied. */
  newExpiresAt: z.iso.datetime(),
});
export type DomainRenewQuote = z.infer<typeof domainRenewQuoteSchema>;

export const domainUpdateRequestSchema = z.object({
  autoRenew: z.boolean(),
});
export type DomainUpdateRequest = z.infer<typeof domainUpdateRequestSchema>;

export const ownedDomainDetailSchema = ownedDomainSchema.extend({
  /** Nameservers the registrar currently publishes; empty when the registrar could not be asked. */
  registrarNameservers: z.array(z.string()),
  /** Our hosted zone, when one exists. */
  zone: z.object({ nameservers: z.array(z.string()) }).nullable(),
  records: z.array(dnsRecordSchema),
});
export type OwnedDomainDetail = z.infer<typeof ownedDomainDetailSchema>;

export const domainSettingsRequestSchema = z
  .object({
    autoRenew: z.boolean().optional(),
    locked: z.boolean().optional(),
    whoisPrivacy: z.boolean().optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: "Nothing to change",
  });
export type DomainSettingsRequest = z.infer<typeof domainSettingsRequestSchema>;

const nameserverSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^([a-z0-9-]+\.)+[a-z0-9-]{2,}$/, { message: "Must be a hostname" });

export const domainNameserversRequestSchema = z.object({
  nameservers: z.array(nameserverSchema).min(2).max(6),
});
export type DomainNameserversRequest = z.infer<typeof domainNameserversRequestSchema>;

export const domainAuthCodeSchema = z.object({
  domain: domainNameSchema,
  authCode: z.string(),
});
export type DomainAuthCode = z.infer<typeof domainAuthCodeSchema>;

// --- Connected (external) domains -----------------------------------------------------------------

export const domainConnectRequestSchema = z.object({ name: domainNameSchema });
export type DomainConnectRequest = z.infer<typeof domainConnectRequestSchema>;

/** The TXT record that proves control of an external domain, and what DNS showed last time. */
export const domainVerificationSchema = z.object({
  domain: domainNameSchema,
  verified: z.boolean(),
  verifiedAt: z.iso.datetime().nullable(),
  record: z.object({
    type: z.literal("TXT"),
    /** Relative to the domain, as most DNS hosts expect it ("_websede"). */
    name: z.string(),
    fqdn: z.string(),
    value: z.string(),
  }),
  /** TXT values found at the record name on the last check; empty before the first check. */
  found: z.array(z.string()),
});
export type DomainVerification = z.infer<typeof domainVerificationSchema>;

export const emailDnsCheckStatusSchema = z.enum(["ok", "missing", "mismatch"]);
export type EmailDnsCheckStatus = z.infer<typeof emailDnsCheckStatusSchema>;

/**
 * How much a record of the email template matters. Required: mail cannot arrive, or leaves
 * unauthenticated, without it. Recommended: mail flows without it, but it helps delivery (DMARC)
 * or lets mail, calendar and contacts apps find the servers by themselves (SRV). Optional: few
 * clients use it today.
 */
export const emailDnsLevelSchema = z.enum(["required", "recommended", "optional"]);
export type EmailDnsLevel = z.infer<typeof emailDnsLevelSchema>;

/** Every record the email template needs, compared with what public DNS answers right now. */
export const emailDnsCheckSchema = z.object({
  domain: domainNameSchema,
  checkedAt: z.iso.datetime(),
  /** Mail can arrive and leave authenticated: every required record (MX, SPF, DKIM) is in place. */
  ready: z.boolean(),
  /** Recommended records missing or different; they never make `ready` false. */
  recommendedMissing: z.number().int().nonnegative(),
  records: z.array(
    z.object({
      type: z.string(),
      name: z.string(),
      fqdn: z.string(),
      /** Presentation form, e.g. "10 mx.example.net" for MX or "10 0 993 imap.example.net" for SRV. */
      expected: z.string(),
      found: z.array(z.string()),
      status: emailDnsCheckStatusSchema,
      level: emailDnsLevelSchema,
    }),
  ),
});
export type EmailDnsCheck = z.infer<typeof emailDnsCheckSchema>;
